import {FC, useCallback, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Close} from '@mui/icons-material';
import {Box, IconButton, Typography} from '@mui/material';

import {DeleteProfileItemDialog} from './DeleteProfileItemDialog';
import {DeletableItem, useEditProfileFormConfig} from './useEditProfileFormConfig';
import FormMaker from '../../../../../components/FormMaker';
import {empionGreen} from '../../../../../layouts/theme/EmpionColors';
import {EducationRequestBody} from '../../../../../lib/api/RequestBodies/features/applicant/profile/EducationRequestBody';
import {WorkExperienceRequestBody} from '../../../../../lib/api/RequestBodies/features/applicant/profile/WorkExperienceRequestBody';
import {ApplicantSkill} from '../../../../../types/applicantSkill/ApplicantSkill';
import {SkillExperience} from '../../../../../types/shared-types';
import {ApplicantProfile, ProfileFormType} from '../../types';

interface Props {
  profile: ApplicantProfile;
  applicantSkills: ApplicantSkill[];
  onQuit: () => void;
  currentForm: {type: ProfileFormType; id?: string} | null;
  // NOTE: This updates the profile data that doesn't need separate API calls.
  onProfileUpdate: (values: Partial<ApplicantProfile>) => void;
  onWorkExperienceCreate: (values: WorkExperienceRequestBody) => void;
  onWorkExperienceUpdate: (values: WorkExperienceRequestBody) => void;
  onWorkExperienceDelete: (id: string) => void;
  onEducationCreate: (values: EducationRequestBody) => void;
  onEducationUpdate: (values: EducationRequestBody) => void;
  onEducationDelete: (id: string) => void;
  onApplicantSkillsUpdate: (skills: {skills: SkillExperience[]}) => void;
}

export const EditProfileForm: FC<Props> = ({
  profile,
  onQuit,
  currentForm,
  onProfileUpdate,
  onWorkExperienceCreate,
  onWorkExperienceUpdate,
  onWorkExperienceDelete,
  onEducationCreate,
  onEducationUpdate,
  onEducationDelete,
  onApplicantSkillsUpdate,
  applicantSkills,
}) => {
  const {t} = useTranslation();
  const [itemToDelete, setItemToDelete] = useState<DeletableItem | null>(null);

  const config = useEditProfileFormConfig({
    profile,
    applicantSkills,
    currentForm,
    onProfileUpdate,
    onWorkExperienceCreate,
    onWorkExperienceUpdate,
    onEducationCreate,
    onEducationUpdate,
    onApplicantSkillsUpdate,
    onRequestDelete: setItemToDelete,
  });

  const onDeleteItem = useCallback(() => {
    if (itemToDelete?.type === 'work-experience') {
      onWorkExperienceDelete(itemToDelete.id);
    } else if (itemToDelete?.type === 'education') {
      onEducationDelete(itemToDelete.id);
    }
    setItemToDelete(null);
  }, [itemToDelete, onEducationDelete, onWorkExperienceDelete]);

  if (!config) {
    return null;
  }

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2}}>
        <Typography variant='h2' sx={{color: empionGreen[700]}}>
          {config.title}
        </Typography>
        <IconButton onClick={onQuit} aria-label='close' sx={{color: empionGreen[700], flexShrink: 0, mt: '-6px'}}>
          <Close fontSize='large' />
        </IconButton>
      </Box>
      <FormMaker
        sections={config.sections}
        onSubmit={config.onSubmit}
        submitLabel={t('shared.formButtons.save') || 'Save'}
        secondaryButton={config.secondaryButton}
      />
      <DeleteProfileItemDialog
        open={!!itemToDelete}
        title={t('applicantProfile.form.deleteDialog.title') || 'Delete'}
        content={t('applicantProfile.form.deleteDialog.content') || 'Are you sure you want to delete this item?'}
        cancelLabel={t('shared.formButtons.cancel') || 'Cancel'}
        confirmLabel={t('shared.formButtons.delete') || 'Delete'}
        onClose={() => setItemToDelete(null)}
        onConfirm={onDeleteItem}
      />
    </Box>
  );
};
